import React, { useState, useEffect } from 'react';
import { Bell, Mail, MessageSquare, Clock, CheckCircle, AlertTriangle, Eye, EyeOff, Info } from 'lucide-react';
import { ApiRoutes } from '../../../api/routes';

interface NotificationRule {
  id: string;
  name: string;
  trigger: 'task_due_soon' | 'task_overdue' | 'task_blocked' | 'client_inactive' | 'milestone_missed';
  channels: ('email' | 'sms' | 'slack')[];
  enabled: boolean;
  schedule: string;
  recipients: string[];
}

interface NotificationHistory {
  id: string;
  ruleId: string;
  ruleName: string;
  trigger: string;
  channel: string;
  recipient: string;
  status: 'sent' | 'failed' | 'pending';
  sentAt: string;
  message: string;
}

interface InAppNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  projectId: string;
  createdAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  graceWindowExpires: string;
  isOutlookEmailSent: boolean;
  recipients: string[];
  priority: 'low' | 'medium' | 'high' | 'critical';
}

export const NotificationSystem: React.FC = () => {
  const [rules, setRules] = useState<NotificationRule[]>([
    {
      id: 'rule-001',
      name: 'Task Due Soon Reminder',
      trigger: 'task_due_soon',
      channels: ['email', 'sms'],
      enabled: true,
      schedule: '24h before due',
      recipients: ['client@example.com', '+1234567890']
    },
    {
      id: 'rule-002',
      name: 'Overdue Task Alert',
      trigger: 'task_overdue',
      channels: ['email', 'slack'],
      enabled: true,
      schedule: 'Daily at 9 AM',
      recipients: ['pm@tyler.com', '#onboarding-alerts']
    },
    {
      id: 'rule-003',
      name: 'Client Inactivity Warning',
      trigger: 'client_inactive',
      channels: ['email'],
      enabled: true,
      schedule: 'After 5 days inactive',
      recipients: ['client@example.com', 'pm@tyler.com']
    }
  ]);

  const [history, setHistory] = useState<NotificationHistory[]>([
    {
      id: 'hist-001',
      ruleId: 'rule-001',
      ruleName: 'Task Due Soon Reminder',
      trigger: 'task_due_soon',
      channel: 'email',
      recipient: 'client@example.com',
      status: 'sent',
      sentAt: '2024-08-24T10:30:00Z',
      message: 'Reminder: Tyler ERP Setup Documentation is due tomorrow'
    },
    {
      id: 'hist-002',
      ruleId: 'rule-002',
      ruleName: 'Overdue Task Alert',
      trigger: 'task_overdue',
      channel: 'slack',
      recipient: '#onboarding-alerts',
      status: 'sent',
      sentAt: '2024-08-24T09:00:00Z',
      message: 'Alert: SSO Configuration task is 2 days overdue'
    }
  ]);

  // Stage 1: In-app notifications with acknowledgment system
  const [inAppNotifications, setInAppNotifications] = useState<InAppNotification[]>([
    {
      id: 'notif-001',
      title: 'File Uploaded Successfully',
      message: 'Tyler_ERP_Requirements.pdf has been uploaded and verified in SharePoint',
      type: 'success',
      projectId: 'proj-001',
      createdAt: new Date().toISOString(),
      graceWindowExpires: new Date(Date.now() + 3 * 60 * 1000).toISOString(), // 3 minutes grace window
      isOutlookEmailSent: false,
      recipients: ['john.smith@tylertech.com', 'pm@tyler.com'],
      priority: 'medium'
    },
    {
      id: 'notif-002',
      title: 'Required Document Submitted',
      message: 'Data Migration Plan has been submitted and is pending review',
      type: 'info',
      projectId: 'proj-001',
      createdAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(), // 2 minutes ago
      graceWindowExpires: new Date(Date.now() + 1 * 60 * 1000).toISOString(), // 1 minute remaining
      isOutlookEmailSent: false,
      recipients: ['sarah.johnson@tylertech.com', 'pm@tyler.com'],
      priority: 'high'
    },
    {
      id: 'notif-003',
      title: 'Message Tagged to Team',
      message: 'New message in SSO Configuration thread requires team attention',
      type: 'warning',
      projectId: 'proj-001',
      createdAt: new Date(Date.now() - 4 * 60 * 1000).toISOString(), // 4 minutes ago
      graceWindowExpires: new Date(Date.now() - 1 * 60 * 1000).toISOString(), // Grace window expired
      isOutlookEmailSent: true, // Outlook email sent after grace window
      recipients: ['pm@tyler.com', 'tech-lead@tyler.com'],
      priority: 'medium'
    }
  ]);

  const [activeTab, setActiveTab] = useState<'rules' | 'history' | 'inapp'>('inapp');

  useEffect(() => {
    // Check for expired grace windows and trigger Outlook emails
    const interval = setInterval(() => {
      checkGraceWindowExpirations();
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [inAppNotifications]);

  const checkGraceWindowExpirations = () => {
    const now = new Date();
    const updatedNotifications = inAppNotifications.map(notification => {
      if (!notification.acknowledgedAt && 
          !notification.isOutlookEmailSent && 
          new Date(notification.graceWindowExpires) < now) {
        // Grace window expired, send Outlook email
        sendOutlookEmail(notification);
        return { ...notification, isOutlookEmailSent: true };
      }
      return notification;
    });
    
    setInAppNotifications(updatedNotifications);
  };

  const sendOutlookEmail = async (notification: InAppNotification) => {
    try {
      // TODO: Implement actual Outlook email sending
      console.log(`Sending Outlook email for notification ${notification.id}:`, notification);
      
      // Emit event for email service
      // this.eventBus.emit('outlook.email.send', {
      //   to: notification.recipients,
      //   subject: notification.title,
      //   body: notification.message,
      //   notificationId: notification.id
      // });
    } catch (error) {
      console.error('Failed to send Outlook email:', error);
    }
  };

  const acknowledgeNotification = async (notificationId: string) => {
    try {
      const response = await ApiRoutes.acknowledgeNotification(notificationId);
      if (response.success) {
        // Update local state
        setInAppNotifications(prev => prev.map(notification => 
          notification.id === notificationId 
            ? { 
                ...notification, 
                acknowledgedAt: new Date().toISOString(),
                acknowledgedBy: 'current-user' // TODO: Get actual user ID
              }
            : notification
        ));
        
        console.log(`Notification ${notificationId} acknowledged successfully`);
      } else {
        console.error('Failed to acknowledge notification:', response.error);
      }
    } catch (error) {
      console.error('Error acknowledging notification:', error);
    }
  };

  const getGraceWindowStatus = (notification: InAppNotification) => {
    const now = new Date();
    const expiresAt = new Date(notification.graceWindowExpires);
    const timeRemaining = expiresAt.getTime() - now.getTime();
    
    if (notification.acknowledgedAt) {
      return { status: 'acknowledged', color: 'text-green-600', icon: CheckCircle };
    }
    
    if (timeRemaining <= 0) {
      return { status: 'expired', color: 'text-red-600', icon: AlertTriangle };
    }
    
    const minutesRemaining = Math.ceil(timeRemaining / (1000 * 60));
    if (minutesRemaining <= 1) {
      return { status: 'expiring-soon', color: 'text-orange-600', icon: Clock };
    }
    
    return { status: 'active', color: 'text-blue-600', icon: Clock };
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      case 'medium': return 'border-blue-500 bg-blue-50';
      case 'low': return 'border-gray-500 bg-gray-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'error': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'info': return <MessageSquare className="w-5 h-5 text-blue-500" />;
      default: return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const toggleRule = (ruleId: string) => {
    setRules(rules.map(rule => 
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
  };

  const sendTestNotification = async (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;

    try {
      const response = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ruleId,
          channels: rule.channels,
          recipients: rule.recipients,
          message: `Test notification for: ${rule.name}`
        }),
      });

      if (response.ok) {
        const newHistoryEntry: NotificationHistory = {
          id: `hist-${Date.now()}`,
          ruleId,
          ruleName: rule.name,
          trigger: rule.trigger,
          channel: rule.channels[0],
          recipient: rule.recipients[0],
          status: 'sent',
          sentAt: new Date().toISOString(),
          message: `Test notification for: ${rule.name}`
        };
        setHistory([newHistoryEntry, ...history]);
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'slack': return <MessageSquare className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'text-[#8A9150] bg-[#8A9150]/10 border-[#8A9150]/20';
      case 'failed': return 'text-red-600 bg-red-100 border-red-200';
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getTriggerIcon = (trigger: string) => {
    switch (trigger) {
      case 'task_due_soon': return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'task_overdue': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'task_blocked': return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      default: return <Bell className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
            Notification Management
          </h2>
          <p className="text-gray-600 text-lg drop-shadow-sm">
            Manage notification rules, view history, and handle in-app notifications
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
          <button
            onClick={() => setActiveTab('inapp')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'inapp'
                ? 'bg-white text-[#4267A1] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Bell className="w-4 h-4" />
              <span>In-App Notifications</span>
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                {inAppNotifications.filter(n => !n.acknowledgedAt).length}
              </span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'rules'
                ? 'bg-white text-[#4267A1] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Rules</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'history'
                ? 'bg-white text-[#4267A1] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>History</span>
            </div>
          </button>
        </div>

        {/* In-App Notifications Tab */}
        {activeTab === 'inapp' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <Info className="w-5 h-5 text-blue-500 mr-2" />
                <span className="text-blue-800 font-medium">
                  In-App Notification System
                </span>
              </div>
              <p className="text-blue-700 text-sm mt-1">
                Notifications are shown in-app with a 3-minute grace window. If not acknowledged within the grace window, 
                an Outlook email will be sent automatically. Acknowledging a notification prevents the email from being sent.
              </p>
            </div>

            <div className="grid gap-4">
              {inAppNotifications.map((notification) => {
                const graceStatus = getGraceWindowStatus(notification);
                const priorityStyle = getPriorityColor(notification.priority);
                
                return (
                  <div
                    key={notification.id}
                    className={`tyler-card border-l-4 ${priorityStyle} ${
                      notification.acknowledgedAt ? 'opacity-75' : ''
                    }`}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-3">
                          {getTypeIcon(notification.type)}
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {notification.title}
                            </h3>
                            <p className="text-gray-600 mb-2">{notification.message}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span>Project: {notification.projectId}</span>
                              <span>Priority: {notification.priority}</span>
                              <span>Recipients: {notification.recipients.length}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {!notification.acknowledgedAt && (
                            <button
                              onClick={() => acknowledgeNotification(notification.id)}
                              className="px-3 py-2 bg-green-100 text-green-800 rounded-md text-sm font-medium hover:bg-green-200 transition-colors"
                            >
                              Acknowledge
                            </button>
                          )}
                          
                          <div className={`flex items-center space-x-1 px-2 py-1 rounded text-sm ${graceStatus.color}`}>
                            <graceStatus.icon className="w-4 h-4" />
                            <span className="font-medium">
                              {graceStatus.status === 'acknowledged' && 'Acknowledged'}
                              {graceStatus.status === 'expired' && 'Grace Expired'}
                              {graceStatus.status === 'expiring-soon' && 'Expiring Soon'}
                              {graceStatus.status === 'active' && 'Active'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <span>Created: {new Date(notification.createdAt).toLocaleString()}</span>
                          {notification.acknowledgedAt && (
                            <span className="text-green-600">
                              Acknowledged: {new Date(notification.acknowledgedAt).toLocaleString()}
                            </span>
                          )}
                          {notification.isOutlookEmailSent && (
                            <span className="text-orange-600 flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              Outlook email sent
                            </span>
                          )}
                        </div>
                        
                        {!notification.acknowledgedAt && graceStatus.status !== 'expired' && (
                          <div className="text-sm">
                            <span className="text-gray-500">Grace window expires: </span>
                            <span className={graceStatus.color}>
                              {new Date(notification.graceWindowExpires).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Rules Tab */}
        {activeTab === 'rules' && (
          <div className="space-y-6">
            <div className="grid gap-4">
              {rules.map((rule) => (
                <div key={rule.id} className="tyler-card">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{rule.name}</h3>
                      <button
                        onClick={() => toggleRule(rule.id)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          rule.enabled
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Trigger:</span> {rule.trigger}
                      </div>
                      <div>
                        <span className="font-medium">Schedule:</span> {rule.schedule}
                      </div>
                      <div>
                        <span className="font-medium">Channels:</span> {rule.channels.join(', ')}
                      </div>
                      <div>
                        <span className="font-medium">Recipients:</span> {rule.recipients.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="tyler-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rule
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trigger
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Channel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sent At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.ruleName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {item.trigger}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {item.channel}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {item.recipient}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            item.status === 'sent' ? 'bg-green-100 text-green-800' :
                            item.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(item.sentAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
